import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { OrderGroupDetailPayload, OrderGroupDetailResponse } from '../models/order-detail.model';

export interface OrderDispatch {
  originName: string;
  dispatchNumber: string;
  currentState: string;
  currentStateDescription: string | null;
  deliveryDate: Date;
  shippingEstimate: Date;
  deliveryStepLabel: string;
  items: {
    skuCode: string;
    name: string;
    measurementUnit: string;
    price: number;
    quantity: number;
    quantityOriginal: number;
    currentState: string;
  }[];
}

export interface OrderWithDispatches {
  orderId: string;
  dispatches: OrderDispatch[];
}

@Injectable({ providedIn: 'root' })
export class OrderProductService {
  private readonly http = inject(HttpClient);
  private readonly endpoint = `${environment.API_ORDERS}`;

  async getOrderProducts(payload: OrderGroupDetailPayload): Promise<OrderGroupDetailResponse | null> {
    try {
      const url = `${this.endpoint}/v2/${payload.tenantId}/orders/${payload.orderGroup}?orderType=${payload.orderType}`;
      const response = await firstValueFrom(
        this.http.get<OrderGroupDetailResponse>(url)
      );
      return response;
    } catch (error) {
      return null;
    }
  }

  static processOrderDispatches(orderGroupDetail: OrderGroupDetailResponse | null): OrderWithDispatches[] {
    if (!orderGroupDetail) return [];

    return orderGroupDetail.orders
      .map(order => {
        const dispatchMap = new Map<string, {
          originName: string;
          dispatchNumber: string;
          currentState: string;
          currentStateDescription: string | null;
          deliveryDate: Date;
          shippingEstimate: Date;
          items: {
            skuCode: string;
            name: string;
            measurementUnit: string;
            price: number;
            quantity: number;
            quantityOriginal: number;
            currentState: string;
          }[];
        }>();

        for (const item of order.details) {
          if (!item.dispatchNumber) continue;
          const key = item.dispatchNumber;
          if (!dispatchMap.has(key)) {
            dispatchMap.set(key, {
              originName: item.originName,
              currentState: item.currentState,
              dispatchNumber: item.dispatchNumber,
              currentStateDescription: item.currentStateDescription,
              deliveryDate: item.deliveryDate,
              shippingEstimate: item.shippingEstimate,
              items: [],
            });
          }

          dispatchMap.get(key)!.items.push({
            skuCode: item.skuCode,
            name: item.name,
            measurementUnit: item.measurementUnit,
            price: item.price,
            quantity: item.quantity,
            quantityOriginal: item.quantityOriginal,
            currentState: item.currentState
          });
        }

        const dispatches = Array.from(dispatchMap.entries()).map(([_, dispatch], index, array) => ({
          ...dispatch,
          deliveryStepLabel: `Entrega ${index + 1} de ${array.length}`,
        }));

        return dispatches.length > 0 ? {
          orderId: order.orderId,
          dispatches,
        } : null;
      })
      .filter((result): result is OrderWithDispatches => !!result);
  }
}
